import { Button, Col, Input, Row, Text } from 'components';
import { Api, getCost, theme, validateEmail } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import GetTheApp from 'screens/GetTheApp';
import { DashboardItem, MonthlyTarget, OptionItem, PickerModal, Total } from './components';
import { filterByType, filterHistory, getDateRange } from './lib';

const Dashboard = (props) => {
  const [total, setTotal] = useState(null),
    [kpiData, setkpiData] = useState([]),
    [selectedOption, setSelectedOption] = useState(0),
    [calendarVisible, setCalendarVisible] = useState(false),
    [email, setEmail] = useState(''),
    user = useSelector((state) => state.user),
    { isMobile } = useSelector((state) => state.appState),
    { paymentHistory, revenueTarget } = user,
    dispatch = useDispatch(),
    { t } = useTranslation(),
    options = ['dashboard.week', 'dashboard.month', 'dashboard.year', 'dashboard.all'];

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (!paymentHistory) return;
    if (selectedOption === 4) return;

    handleFilterChange(null, selectedOption);
  }, [selectedOption]);

  function handleCalendarVisible() {
    setCalendarVisible(!calendarVisible);
  }

  function handleFilterChange(values, idx) {
    const newRange = getDateRange(values, idx);

    const filtered = filterHistory(paymentHistory, newRange.range, !!newRange.all);

    setkpiData(
      ['shoutout', 'connect', 'referral', 'subscription'].map((type) => {
        return filterByType(filtered, type, 2);
      })
    );

    const reduced = filtered.reduce((x, y) => {
      const cost = getCost(y.rates, y.currency, y.amount);

      return x + (cost.convertedPrice - (y.fee / 100) * cost.convertedPrice) / 100;
    }, 0);

    setTotal(reduced);
  }

  async function fetchHistory() {
    const res = await Api.post('/users/getPaymentHistory');
    if (!res) return;

    dispatch({
      type: 'SET_PAYMENT_HISTORY',
      payload: res
    });
  }

  async function sendInvite() {
    if (!validateEmail(email)) {
      alert('hele');
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: 'Please enter a valid email.' }
      });

      return;
    }

    const res = await Api.post('/users/invite', { email }, 'dashboardInvite');
    if (!res) return;

    setEmail('');
  }

  if (isMobile) return <GetTheApp alwaysShow />;

  return (
    <Col wid="100%" centerAll ht="calc(100vh - 73px)" bg="#F3F5F8" start>
      <Row marg="40px 0 0" noFlex wid={isMobile ? '100vw' : '936px'} start>
        <Col>
          <Row between center wid="100%">
            <Text h1>{t('dashboard.dashboard')}</Text>

            <Row gap="8px">
              {options.map((x, i) => (
                <OptionItem
                  key={i}
                  index={i}
                  option={x}
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                />
              ))}

              <OptionItem
                onClick={handleCalendarVisible}
                option={t('dashboard.custom')}
                index={4}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
              />

              {calendarVisible && (
                <PickerModal
                  handleClose={() => setCalendarVisible(false)}
                  setSelectedOption={setSelectedOption}
                  handleFilterChange={handleFilterChange}
                />
              )}
            </Row>
          </Row>

          <Row between wid="100%" marg="23px 0 0" gap="24px">
            <Total total={total} data={kpiData} />

            <Col start gap="24px">
              <MonthlyTarget revenueTarget={revenueTarget} paymentHistory={paymentHistory} />

              <DashboardItem
                title={t('dashboard.referrals')}
                bg={theme.DARK_PURPLE}
                value1Type="currency"
                valueTitle1={t('dashboard.income')}
                valueTitle2={t('dashboard.users')}
                big
                dark
                data={kpiData.find((x) => x.type === 'referral') || []}
              />

              <DashboardItem
                title={t('settings.subscriptions')}
                bg={theme.GREY_20}
                value1Type="currency"
                valueTitle1={t('dashboard.income')}
                valueTitle2={t('common.subscribed')}
                data={kpiData.find((x) => x.type === 'subscription') || []}
                value2={0}
                small
              />

              <DashboardItem
                title={t('dashboard.advertising')}
                bg={theme.GREEN}
                value1={0}
                value1Type="currency"
                valueTitle1={t('dashboard.income')}
                valueTitle2={t('dashboard.views')}
                value2={0}
                small
              />
            </Col>

            <Col start gap="24px">
              <DashboardItem
                title={t('dashboard.shoutouts')}
                type="shoutout"
                bg={theme.SECONDARY}
                paymentHistory={paymentHistory}
                valueTitle1={t('dashboard.income')}
                valueTitle2={t('dashboard.number')}
                value1Type="currency"
                data={kpiData.find((x) => x.type === 'shoutout') || []}
                dark
              />

              <Col wid="100%">
                <Text h2>{t('dashboard.inviteAthletes')}</Text>

                <Col
                  marg="8px 0 0"
                  ht="92px"
                  noFlex
                  wid="calc(100% - 32px)"
                  bg={theme.SECONDARY}
                  pad="12px 16px 16px"
                  between
                >
                  <Text col="#fff">{t('dashboard.useAthleteRefferalCodes')}</Text>

                  <Row wid="100%" gap="12px" center>
                    <Input
                      ht="40px"
                      wid="171px"
                      horizontalCenter
                      value={email}
                      onChange={({ target }) => setEmail(target.value)}
                      onSubmit={sendInvite}
                      placeholder={t('dashboard.athleteEmail')}
                      bg="#fff"
                    />

                    <Button
                      wid="81px"
                      text={t('common.send')}
                      disabled={!validateEmail(email)}
                      onClick={sendInvite}
                      action="dashboardInvite"
                    />
                  </Row>
                </Col>
              </Col>

              <DashboardItem
                title={t('dashboard.vipConnect')}
                type="connect"
                bg={theme.PINK}
                paymentHistory={paymentHistory}
                data={kpiData.find((x) => x.type === 'connect') || []}
                value1Type="currency"
                valueTitle1={t('dashboard.income')}
                valueTitle2={t('dashboard.meetings')}
                small
                dark
              />

              <DashboardItem
                title={t('dashboard.store')}
                bg={theme.TALE}
                value1={0}
                value1Type="currency"
                valueTitle1={t('dashboard.income')}
                valueTitle1={t('dashboard.itemsSold')}
                value2={0}
                small
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Col>
  );
};

export default Dashboard;
